<script lang="ts" setup>
import { ICON_VARIANTS } from '#components/elements/input';
import InputDateMonth from '#components/elements/input/inputDate/inputDateMonth/InputDateMonth.vue';
import { useLanguage } from '#composables/useLanguage';
import type { PropType } from 'vue';
import { computed } from 'vue';
import { normalizeMonthDateInterval } from '../periodSelectorUtils';
import type { DateIntervalType } from '../types/dateInterval.type';
import type { SelectedIntervalType } from '../types/selectedInterval.type';

const { t } = useLanguage();

const props = defineProps({
  availableInterval: { type: Object as PropType<DateIntervalType>, required: true },
  selectedInterval: { type: Object as PropType<SelectedIntervalType>, required: true },
  disabled: { type: Boolean, default: false }
});
const emit = defineEmits<{
  updateInterval: [interval: SelectedIntervalType];
}>();

const selectedIntervalData = computed(() => [props.selectedInterval.from, props.selectedInterval.to]);

function onUpdateInterval(values: [from: Date, to: Date]) {
  emit('updateInterval', normalizeMonthDateInterval(values));
}

const getAvailableDateMin = computed(() => {
  const from = selectedIntervalData.value[0];
  if (props.availableInterval.start && from && from > props.availableInterval.start) {
    return from;
  }

  return props.availableInterval.start;
});

const getAvailableDateMax = computed(() => {
  const to = selectedIntervalData.value[1];
  if (props.availableInterval.end && to && to < props.availableInterval.end) {
    return to;
  }

  return props.availableInterval.end;
});

const getVariant = computed(() => {
  const from = selectedIntervalData.value[0];
  const to = selectedIntervalData.value[1];
  return selectedIntervalData.value?.length > 1 && from && to && from > to ? 'error' : 'default';
});
</script>

<template>
  <div class="flex flex-col justify-between items-center gap-4">
    <div class="flex flex-row items-center gap-5 w-full">
      <div class="w-10 capitalize">{{ t(`commonLabel.from`) }}</div>
      <div class="flex-1 w-full">
        <InputDateMonth
          :model-value="selectedIntervalData[0]"
          name="time-selector-month-start"
          :placeholder="'MM/YYYY'"
          :date-min="availableInterval.start"
          :date-max="getAvailableDateMax"
          :start-date="availableInterval?.end"
          :icon-right="'calendar'"
          :icon-right-variant="ICON_VARIANTS[1]"
          :disabled="disabled"
          test-id="time-selector-month-start"
          @update:model-value="selectedIntervalData[1] && onUpdateInterval([$event, selectedIntervalData[1]])"
        />
      </div>
    </div>

    <div class="flex flex-row items-center gap-5 w-full">
      <div class="w-10 capitalize">{{ t(`commonLabel.to`) }}</div>
      <div class="flex-1 w-full">
        <InputDateMonth
          :model-value="selectedIntervalData[1]"
          name="time-selector-month-end"
          :placeholder="'MM/YYYY'"
          :date-min="getAvailableDateMin"
          :date-max="availableInterval.end"
          :start-date="availableInterval?.end"
          :icon-right="'calendar'"
          :icon-right-variant="ICON_VARIANTS[1]"
          :variant="getVariant"
          :disabled="disabled"
          test-id="time-selector-month-end"
          @update:model-value="selectedIntervalData[0] && onUpdateInterval([selectedIntervalData[0], $event])"
        />
      </div>
    </div>
  </div>
</template>
